import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router';

import {
  FALLBACK_PRIZE_LINES,
  FALLBACK_SEASON_TEXT,
  currentPrizeLines,
  currentSeasonText,
  prizePhraseOf,
  useDrawsStore,
} from '@/entities/draw';
import type { PrizeLine } from '@/entities/draw';
import type { Leaderboard, LeaderboardEntry } from '@/entities/leaderboard';
import { useLeaderboardStore } from '@/entities/leaderboard';
import { useSessionStore } from '@/entities/session';
import { formatScore } from '@/shared/lib';
import { RADIUS, Skeleton, Squircle, StaticSquircle } from '@/shared/ui';

import styles from './LeaderboardList.module.scss';

type Period = 'week' | 'month';
type RowVariant = 'top' | 'normal' | 'you-top' | 'you-not-top';

/** «Рейтинг» (узел 438:51328, фрейм 438:51329). */
export function LeaderboardList() {
  // Период приходит из адреса: на Рейтинг ведут две разные кнопки бота
  // («Неделя» и «Месяц»), и каждая обязана открыть свою вкладку.
  // Читается один раз, при монтировании — дальше переключателем владеет
  // игрок, и переписывать под него адрес незачем.
  const [searchParams] = useSearchParams();
  const [period, setPeriod] = useState<Period>(
    searchParams.get('period') === 'month' ? 'month' : 'week',
  );

  const displayName = useSessionStore((state) => state.user?.displayName ?? state.user?.firstName);
  const weekly = useLeaderboardStore((state) => state.weekly);
  const monthly = useLeaderboardStore((state) => state.monthly);
  // Флаги берутся у периода, который открыт: иначе скелет недельной
  // таблицы держался бы, пока грузится месячная.
  const weeklyIsLoading = useLeaderboardStore((state) => state.weeklyIsLoading);
  const weeklyError = useLeaderboardStore((state) => state.weeklyError);
  const monthlyIsLoading = useLeaderboardStore(
    (state) => state.monthlyIsLoading,
  );
  const monthlyError = useLeaderboardStore((state) => state.monthlyError);
  const loadWeekly = useLeaderboardStore((state) => state.loadWeekly);
  const loadMonthly = useLeaderboardStore((state) => state.loadMonthly);

  const currentDraws = useDrawsStore((state) => state.current);
  const loadCurrentDraws = useDrawsStore((state) => state.loadCurrent);

  useEffect(() => {
    void loadWeekly();
    void loadMonthly();
  }, [loadWeekly, loadMonthly]);

  // Розыгрыши нужны только на вкладке «Месячный» — грузим, когда открыли
  useEffect(() => {
    if (period !== 'month') return;
    void loadCurrentDraws();
  }, [period, loadCurrentDraws]);

  const isWeek = period === 'week';
  const data = isWeek ? weekly : monthly;
  const isLoading = isWeek ? weeklyIsLoading : monthlyIsLoading;
  const error = isWeek ? weeklyError : monthlyError;

  return (
    <div className={styles.root}>
      <Squircle cornerRadius={RADIUS.xl} className={styles.tabs} role="tablist">
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'week'}
            className={clsx(styles.tab, period === 'week' && styles.tabActive)}
            onClick={() => { setPeriod('week'); }}
          >
            Еженедельный
          </button>
        </Squircle>
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'month'}
            className={clsx(styles.tab, period === 'month' && styles.tabActive)}
            onClick={() => { setPeriod('month'); }}
          >
            Месячный
          </button>
        </Squircle>
      </Squircle>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      {isLoading && !data ? <Skeleton count={6} height={46} /> : null}

      {data && period === 'week' && data.prizeTiers.length > 0 ? (
        <PrizeAccordion title="Призы недели" tone="highlight" defaultOpen testId="rating-prizes">
          <ul className={styles.tierList}>
            {data.prizeTiers.map((tier) => (
              <li className={styles.tierRow} key={`${String(tier.rankFrom)}-${String(tier.rankTo)}`}>
                <span className={styles.tierLabel}>
                  {tier.rankFrom === tier.rankTo
                    ? `Место ${String(tier.rankFrom)}`
                    : `Места ${String(tier.rankFrom)}-${String(tier.rankTo)}`}
                </span>
                <span className={styles.tierValues}>
                  {tier.freebetReward > 0 ? (
                    <span className={styles.tierValue}>
                      <img className={styles.freebetIcon} src="/images/header/freebet-white.svg" alt="" aria-hidden />
                      {tier.freebetReward}
                    </span>
                  ) : null}
                  <span className={styles.tierValue}>
                    <img className={styles.fireIcon} src="/images/header/fire-white.svg" alt="" aria-hidden />
                    {tier.pointsReward}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </PrizeAccordion>
      ) : null}

      {/* Призы месяца / Финальный приз (438:51475). Данные приходят из
          `/draws/*`, но только ПОСЛЕ жеребьёвки — пока цикл идёт, ответ
          пустой, и остаётся текст макета как запасной вариант. */}
      {data && period === 'month' ? (
        <>
          <PrizeAccordion title="Призы месяца" tone="info" defaultOpen testId="rating-month-prizes">
            <MonthPrizeText
              lines={currentPrizeLines(currentDraws) ?? FALLBACK_PRIZE_LINES}
            />
          </PrizeAccordion>
          <PrizeAccordion title="Финальный приз" tone="success" testId="rating-final-prize">
            <p className={styles.prizeText}>
              {currentSeasonText(currentDraws) ?? FALLBACK_SEASON_TEXT}
            </p>
          </PrizeAccordion>
        </>
      ) : null}

      {data ? <LeaderboardTable data={data} displayName={displayName} /> : null}
    </div>
  );
}

function MonthPrizeText({ lines }: { lines: PrizeLine[] }) {
  return (
    <p className={styles.prizeText}>
      {lines.map((line, index) => {
        const phrase = prizePhraseOf(line);
        return (
          <span key={line.poolType}>
            {index > 0 ? (
              <>
                <br />
                <br />
              </>
            ) : null}
            {phrase.before}
            <span className={styles.highlight}>{phrase.pool}</span>
            {phrase.between}
            <span className={styles.highlight}>{phrase.prize}</span>
          </span>
        );
      })}
    </p>
  );
}

const TONE_CLASS = {
  highlight: 'toneHighlight',
  info: 'toneInfo',
  success: 'toneSuccess',
} as const satisfies Record<string, keyof typeof styles>;

function PrizeAccordion({
  title,
  tone,
  defaultOpen = false,
  testId,
  children,
}: {
  title: string;
  tone: keyof typeof TONE_CLASS;
  defaultOpen?: boolean;
  testId?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Squircle
      cornerRadius={RADIUS.xl}
      className={clsx(styles.prizes, styles[TONE_CLASS[tone]])}
      data-testid={testId}
    >
      <button
        type="button"
        className={styles.prizesHeader}
        aria-expanded={open}
        onClick={() => { setOpen((value) => !value); }}
      >
        <h2 className={styles.prizesTitle}>{title}</h2>
        <StaticSquircle asChild width={24} height={24} cornerRadius={RADIUS.sm}>
          <span className={styles.chevronBox}>
            <img
              // chevron-open.svg смотрит вниз, chevron-closed.svg — вверх
              // (см. тот же комментарий в FaqList.tsx).
              src={open ? '/images/icons/chevron-closed.svg' : '/images/icons/chevron-open.svg'}
              alt=""
              aria-hidden
              className={styles.chevron}
            />
          </span>
        </StaticSquircle>
      </button>

      {open ? children : null}
    </Squircle>
  );
}

function LeaderboardTable({
  data,
  displayName,
}: {
  data: Leaderboard;
  displayName: string | undefined;
}) {
  const meRowRef = useRef<HTMLLIElement>(null);
  const [meVisible, setMeVisible] = useState(false);
  const meEntry = data.entries.find((entry) => entry.rank === data.me.rank) ?? null;

  // Плашка переключается чуть раньше, чем строку реально закрывает нижний
  // бар/бейдж — EARLY_TRIGGER_PX срезает от «видимой» области снизу, чтобы
  // переход начинался, пока строка ещё физически на экране, а не в момент,
  // когда её уже наполовину съело перекрытие.
  const EARLY_TRIGGER_PX = 56;

  // useLayoutEffect, не useEffect: IntersectionObserver сообщает о видимости
  // асинхронно, первым же кадром — до его колбэка строка «я» вне экрана
  // (например, скрыта под нижним баром) читалась как видимая, и закреплённая
  // плашка не появлялась, пока игрок не проскроллит хоть немного. Синхронный
  // getBoundingClientRect до пейнта даёт верное состояние сразу, обсёрвер
  // дальше просто следит за скроллом.
  useLayoutEffect(() => {
    const row = meRowRef.current;
    if (!meEntry || !row) {
      setMeVisible(false);
      return;
    }

    const scrollRoot = row.closest('main');
    if (!scrollRoot) {
      setMeVisible(false);
      return;
    }

    const isRowVisible = (): boolean => {
      const rowRect = row.getBoundingClientRect();
      const rootRect = scrollRoot.getBoundingClientRect();
      const effectiveBottom = rootRect.bottom - EARLY_TRIGGER_PX;
      const visibleHeight = Math.min(rowRect.bottom, effectiveBottom) - Math.max(rowRect.top, rootRect.top);
      return visibleHeight >= rowRect.height * 0.5;
    };
    setMeVisible(isRowVisible());

    const observer = new IntersectionObserver(
      ([visibleEntry]) => { setMeVisible(visibleEntry?.isIntersecting ?? false); },
      { root: scrollRoot, rootMargin: `0px 0px -${String(EARLY_TRIGGER_PX)}px 0px`, threshold: 0.5 },
    );
    observer.observe(row);
    return () => { observer.disconnect(); };
  }, [meEntry]);

  const scrollToMe = (): void => {
    meRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <>
      <Squircle cornerRadius={RADIUS.xl} className={styles.table} data-testid="rating-table">
        <div className={styles.sheetHeader}>
          <span className={styles.colRank}>Место</span>
          <span className={styles.colName}>Ник</span>
          <span className={styles.colScore}>Счёт</span>
        </div>

        {data.entries.length === 0 ? (
          <p className={styles.empty} data-testid="rating-empty">
            Пока никто не набрал Очков — стань первым
          </p>
        ) : (
          <ul className={styles.rows}>
            {data.entries.map((entry) => {
              const isMe = entry.rank === data.me.rank;
              const variant: RowVariant = isMe
                ? entry.rank <= 10 ? 'you-top' : 'you-not-top'
                : entry.rank <= 10 ? 'top' : 'normal';

              return (
                <LeaderboardRow
                  key={entry.userId}
                  entry={entry}
                  variant={variant}
                  rowRef={isMe ? meRowRef : undefined}
                />
              );
            })}
          </ul>
        )}
      </Squircle>

      {/* Резерв под закреплённую плашку «я» — она position: fixed и не
          толкает контент, поэтому без спейсера перекрывала бы последние
          строки списка, когда они уже не помещаются на экране. */}
      {data.me.rank !== null ? <div className={styles.pinnedMeSpacer} aria-hidden /> : null}

      {/* «я» вне видимой области (438:51328, state=you) — закреплена внизу
          холста тем же приёмом, что AppHeader/BottomBar (position: fixed
          внутри .layout с transform: scale создаёт свой containing block).
          Смонтирована всегда — плавное появление/скрытие идёт CSS-переходом
          по классу, а не монтированием/размонтированием без анимации. */}
      {data.me.rank !== null ? (
        <button
          type="button"
          className={clsx(styles.pinnedMe, !meVisible && styles.pinnedMeShown)}
          onClick={meEntry ? scrollToMe : undefined}
          aria-hidden={meVisible}
          tabIndex={meVisible ? -1 : 0}
          data-testid="rating-me-pinned"
        >
          <span className={styles.rank}>{data.me.rank}</span>
          <span className={styles.name}>@{displayName ?? 'игрок'}</span>
          <span className={styles.score}>{formatScore(data.me.points)}</span>
        </button>
      ) : null}
    </>
  );
}

function LeaderboardRow({
  entry,
  variant,
  rowRef,
}: {
  entry: LeaderboardEntry;
  variant: RowVariant;
  rowRef?: React.Ref<HTMLLIElement> | undefined;
}) {
  const showIcon = variant === 'top' || variant === 'you-top';
  const isYou = variant === 'you-top' || variant === 'you-not-top';

  return (
    <li ref={rowRef} className={clsx(styles.row, isYou && styles.rowYou)} data-testid="rating-row">
      <span className={styles.rank}>
        {showIcon ? (
          <img
            className={styles.rankIcon}
            src={variant === 'you-top' ? '/images/icons/fire-pink.svg' : '/images/header/fire.svg'}
            alt=""
            aria-hidden
          />
        ) : null}
        <span className={clsx(styles.rankNumber, showIcon && styles.rankNumberWithIcon)}>{entry.rank}</span>
      </span>
      <span className={styles.name}>{entry.displayName ?? '@игрок'}</span>
      <span className={styles.score}>{formatScore(entry.points)}</span>
    </li>
  );
}
